import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {GetApp as DownloadIcon} from '@mui/icons-material';

import MainCard from 'components/MainCard';
import { fetchPaginatedEmployees } from 'api/employees';
import { useSubStores } from 'contexts/SubStoreContext';
import { getSchedule } from 'api/schedule';
import { getEmployeeVacations } from 'api/employeeVacations';
import { getSwapShiftRequests } from 'api/schedule';

function toISO(date) {
  try {
    const d = new Date(date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  } catch (e) {
    return '';
  }
}

// Convert decimal hours to "Xhr Ymin" format
function formatHoursToHrMin(decimalHours) {
  if (decimalHours === null || decimalHours === undefined || isNaN(decimalHours)) {
    return '0hr 0min';
  }
  const num = typeof decimalHours === 'string' ? parseFloat(decimalHours) : decimalHours;
  const hours = Math.floor(Math.abs(num));
  const minutes = Math.round((Math.abs(num) - hours) * 60);
  const sign = num < 0 ? '-' : '';
  if (minutes === 0) {
    return `${sign}${hours}hr`;
  }
  return `${sign}${hours}hr ${minutes}min`;
}

function toTime(time) {
  try {
    const d = new Date(time);
    return `${d.getHours()}:${d.getMinutes()}`;
  } catch (e) {
    return '';
  }
}
//get the swap shift requests for an employee
const getSwapShiftRequestsByEmployee = async (employeeId) => {
  const data = await getSwapShiftRequests (employeeId);
  return data;
};



function parseHours(text) {
  // accepts strings like "8h" or numbers; returns number of hours
  if (typeof text === 'number') return text;
  if (!text) return 0;
  const match = String(text).match(/(-?\d+(?:\.\d+)?)\s*h?/i);
  return match ? parseFloat(match[1]) : 0;
}

export default function EmployeeDashboard() {
  const { substores } = useSubStores();
  const [substoreOptions, setSubstoreOptions] = useState([]);
  const [substoreFilter, setSubstoreFilter] = useState('');
  const [dateFrom, setDateFrom] = useState(() => {
    const today = new Date();
    return today.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState(() => {
    const today = new Date();
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 6);
    return nextWeek.toISOString().slice(0, 10);
  });

  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [employees, setEmployees] = useState([]);

  const [shiftsLoading, setShiftsLoading] = useState(false);
  const [scheduleShifts, setScheduleShifts] = useState([]);

  useEffect(() => {
    if (Array.isArray(substores) && substores.length > 0) {
      setSubstoreOptions(substores.map(s => ({ id: s.id, name: s.storeName || s.name })));
      // Set default to "Main" if present
      const mainSubstore = substores.find(
        s => (s.storeName || s.name || '').toLowerCase() === 'main'
      );
      if (mainSubstore) {
        setSubstoreFilter(mainSubstore.id);
      } else {
        setSubstoreFilter(substores[0].id); // fallback to first
      }
    }
  }, [substores]);

  useEffect(() => {
    const load = async () => {
      setLoadingEmployees(true);
      try {
        const params = { page: 1, page_size: 100, ...(substoreFilter ? { substore: substoreFilter } : {}) };
        const data = await fetchPaginatedEmployees(params);
        setEmployees(data.results || []);
      } catch (e) {
        setEmployees([]);
      } finally {
        setLoadingEmployees(false);
      }
    };
    load();
  }, [substoreFilter]);

  // Load shifts from backend schedule API; supports All Substores by merging
  useEffect(() => {
    const loadShifts = async () => {
      setShiftsLoading(true);
      try {
        const substoreIds = substoreFilter
          ? [parseInt(substoreFilter, 10)]
          : substoreOptions.map(s => s.id).filter(Boolean);
        if (substoreIds.length === 0) {
          setScheduleShifts([]);
          return;
        }

        const results = await Promise.all(
          substoreIds.map(async (sid) => {
            const data = await getSchedule(sid, dateFrom, dateTo);
            const substoreName = data.substore?.name || substoreOptions.find(s => s.id === sid)?.name || '';
            const mapped = (data.schedule || []).map((shift) => {
              const startDateTime = `${shift.date}T${shift.start_time}`;
              const endDateTime = `${shift.date}T${shift.end_time}`;
              return {
                id: shift.shift_id || shift.id,
                shift_id: shift.shift_id || shift.id,
                date: shift.date, // Preserve date for per-employee time calculation
                startTime: startDateTime,
                endTime: endDateTime,
                required_employee: shift.required_employee,
                substore_id: sid,
                substore: substoreName,
                assigned_employees: (shift.assigned_employees || []).map((e) => {
                  const validId = e.id || e.employee_id || e.user_id || e.userId;
                  // Preserve per-employee scheduled times from ScheduleAssignment
                  const empStartTime = e.start_time || shift.start_time;
                  const empEndTime = e.end_time || shift.end_time;
                  return { 
                    ...e, 
                    id: validId,
                    start_time: empStartTime,
                    end_time: empEndTime
                  };
                }).filter(e => !!e.id)
              };
            });
            return mapped;
          })
        );
        setScheduleShifts(results.flat());
      } catch (e) {
        setScheduleShifts([]);
      } finally {
        setShiftsLoading(false);
      }
    };
    loadShifts();
  }, [substoreFilter, substoreOptions, dateFrom, dateTo]);

  // Aggregate hours per employee from shifts
  const rows = useMemo(() => {
    const byEmployee = new Map();
    const calcDurationHrs = (startIso, endIso) => {
      const start = new Date(startIso);
      const end = new Date(endIso);
      let diff = (end - start) / (1000 * 60 * 60);
      if (diff < 0) {
        const endNext = new Date(start);
        endNext.setDate(endNext.getDate() + 1);
        endNext.setHours(end.getHours(), end.getMinutes(), end.getSeconds(), end.getMilliseconds());
        diff = (endNext - start) / (1000 * 60 * 60);
      }
      return Math.max(0, diff);
    };

    scheduleShifts.forEach(shift => {
      (shift.assigned_employees || []).forEach(emp => {
        const empId = emp.id;
        if (!empId) return;
        
        // Use per-employee scheduled times if available, otherwise fallback to shift times
        const empStartTime = emp.start_time 
          ? `${shift.date || shift.startTime?.split('T')[0]}T${emp.start_time}`
          : shift.startTime;
        const empEndTime = emp.end_time 
          ? `${shift.date || shift.endTime?.split('T')[0]}T${emp.end_time}`
          : shift.endTime;
        
        // Calculate duration using per-employee times
        const durationHrs = calcDurationHrs(empStartTime, empEndTime);
        
        const rec = byEmployee.get(empId) || { hoursWorked: 0, shifts: [] };
        rec.hoursWorked += durationHrs;
        rec.shifts.push({
          id: shift.id,
          startTime: empStartTime,
          endTime: empEndTime,
          duration: `${Math.round(durationHrs)}h`,
          status: 'Scheduled',
          substore: shift.substore,
          // Store per-employee times for display
          assignment_start_time: emp.start_time,
          assignment_end_time: emp.end_time
        });
        byEmployee.set(empId, rec);
      });
    });

    const numDays = (() => {
      try {
        const a = new Date(dateFrom);
        const b = new Date(dateTo);
        a.setHours(0,0,0,0);
        b.setHours(0,0,0,0);
        const diff = Math.round((b - a) / (1000 * 60 * 60 * 24)) + 1;
        return Math.max(1, diff);
      } catch (e) { return 1; }
    })();

    return employees.map(emp => {
      const rec = byEmployee.get(emp.id) || { hoursWorked: 0, shifts: [] };
      const expectedPerDay = parseHours(emp.work_hours);
      const totalHours = expectedPerDay * numDays / 7;
      const extra = rec.hoursWorked - totalHours;
      return {
        id: emp.id,
        name: emp.fullName || emp.username,
        store: emp.substore?.name || substoreOptions.find(s => s.id === emp.substoreId)?.name || 'Unknown',
        totalHours: totalHours.toFixed(2),
        hoursWorked: rec.hoursWorked.toFixed(2),
        extraHours: extra.toFixed(2),
        shifts: rec.shifts
      };
    });
  }, [employees, scheduleShifts, substoreOptions, dateFrom, dateTo]);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewEmployee, setViewEmployee] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0: Shifts, 1: Leaves
  const [leavesLoading, setLeavesLoading] = useState(false);
  const [leaves, setLeaves] = useState([]);

  const openView = async (row) => {
    setViewEmployee(row);
    setActiveTab(0);
    setLeaves([]);
    setViewOpen(true);
    
    // Fetch leaves data immediately when dialog opens
    try {
      setLeavesLoading(true);
      const data = await getEmployeeVacations(row.id);
      setLeaves(Array.isArray(data) ? data : (data.results || []));
    } catch (e) {
      console.error('Failed to fetch leaves:', e);
      setLeaves([]);
    } finally {
      setLeavesLoading(false);
    }
    // Fetch swap shift requests for this employee and attach to viewEmployee
    try {
      const swapRaw = await getSwapShiftRequestsByEmployee(row.id);
      const swapArr = Array.isArray(swapRaw) ? swapRaw : (swapRaw.results || []);
      // Normalize swap requests with shift details from updated serializer
      const normalized = swapArr.map(s => {
        // Handle nested shift object (from updated serializer)
        const shiftObj = s.shift || {};
        const date = shiftObj.date || '';
        const startTime = shiftObj.start_time || shiftObj.startTime || '';
        const endTime = shiftObj.end_time || shiftObj.endTime || '';
        
        // Calculate duration
        let duration = '';
        if (date && startTime && endTime) {
          try {
            const start = new Date(`${date}T${startTime}`);
            const end = new Date(`${date}T${endTime}`);
            let diff = (end - start) / (1000 * 60 * 60); // hours
            // Handle overnight shifts
            if (diff < 0) {
              const nextDay = new Date(start);
              nextDay.setDate(nextDay.getDate() + 1);
              const nextDayEnd = new Date(`${nextDay.toISOString().split('T')[0]}T${endTime}`);
              diff = (nextDayEnd - start) / (1000 * 60 * 60);
            }
            duration = `${Math.round(diff)}h`;
          } catch (e) {
            duration = 'N/A';
          }
        }
        
        // Handle target employee
        const target = s.target_employee || {};
        const targetUsername = target.username || target.email || target.id || 'Unknown';
        
        return {
          id: s.id || `${row.id}-${date}-${startTime}`,
          shift: { 
            date, 
            startTime: startTime, 
            endTime: endTime, 
            duration 
          },
          target_employee: { username: targetUsername }
        };
      });
      // Filter by selected date range
      const filtered = normalized.filter(item => {
        if (!item.shift || !item.shift.date) return false;
        const sd = new Date(item.shift.date);
        const from = new Date(dateFrom);
        const to = new Date(dateTo);
        from.setHours(0,0,0,0);
        to.setHours(23,59,59,999);
        sd.setHours(0,0,0,0);
        return sd >= from && sd <= to;
      });
      // attach to current viewEmployee state
      setViewEmployee(prev => ({ ...(prev || row), swapRequests: filtered }));
    } catch (err) {
      console.error('Failed to fetch swap requests:', err);
      setViewEmployee(prev => ({ ...(prev || row), swapRequests: [] }));
    }
  };
  const closeView = () => {
    setViewOpen(false);
    setViewEmployee(null);
  };

  const loading = loadingEmployees || shiftsLoading;

  // Note: Leaves data is now fetched immediately when dialog opens in openView function

const handleExportPDF = () => {
  // Create new PDF document
  const pdf = new jsPDF('landscape', 'pt', 'a4');
  
  // Get current date for report
  const currentDate = new Date().toLocaleDateString();
  const reportTitle = 'Employee Dashboard Report';
  const dateRange = `From: ${dateFrom} To: ${dateTo}`;
  const selectedSubstore = substoreOptions.find(s => s.id === substoreFilter)?.name || 'All Substores';
  
  // Add title and metadata
  pdf.setFontSize(18);
  pdf.text(reportTitle, 40, 40);
  
  pdf.setFontSize(12);
  pdf.text(dateRange, 40, 65);
  pdf.text(`Substore: ${selectedSubstore}`, 40, 80);
  pdf.text(`Total Employees: ${rows.length}`, 40, 95);
  pdf.text(`Generated on: ${currentDate}`, 40, 110);
  
  // Prepare table data
  const tableColumns = [
    'Name',
    //'Store', 
    'Total Hours',
    'Hours Worked',
    'Extra Hours'
  ];
  
  const tableData = rows.map(row => [
    row.name,
    //row.store,
    formatHoursToHrMin(row.totalHours),
    formatHoursToHrMin(row.hoursWorked),
    (parseFloat(row.extraHours) >= 0 ? '+' : '') + formatHoursToHrMin(row.extraHours)
  ]);
  
  // Add table to PDF
  const tableResult = autoTable(pdf, {
    head: [tableColumns],
    body: tableData,
    startY: 125,
    styles: {
      fontSize: 10,
      cellPadding: 5,
    },
    headStyles: {
      fillColor: [41, 128, 185],
      textColor: 255,
      fontStyle: 'bold'
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245]
    },
    margin: { top: 110, right: 40, bottom: 40, left: 40 },
    tableWidth: 'auto',
  });
  
  // Add summary at the bottom
  const finalY = (tableResult && tableResult.finalY) ? tableResult.finalY + 20 : 150;
  pdf.setFontSize(10);
  
  // Save the PDF
  pdf.save(`employee_dashboard_report_${dateFrom}_to_${dateTo}.pdf`);
};

const handleExportEmployeePDF = async () => {
  if (!viewEmployee) return;
  
  // Use already loaded leaves data
  const employeeLeaves = leaves;
  
  // Create new PDF document
  const pdf = new jsPDF('portrait', 'pt', 'a4');
  
  // Get current date for report
  const currentDate = new Date().toLocaleDateString();
  const reportTitle = `${viewEmployee.name} - Employee Report`;
  const dateRange = `From: ${dateFrom} To: ${dateTo}`;
  
  // Add title and metadata
  pdf.setFontSize(18);
  pdf.text(reportTitle, 40, 40);
  
  pdf.setFontSize(12);
  pdf.text(dateRange, 40, 65);
  pdf.text(`Substore: ${viewEmployee.store}`, 40, 80);
  pdf.text(`Generated on: ${currentDate}`, 40, 95);
  
   let currentY = 125;
   
   // Add Shift Details Section
   pdf.setFontSize(14);
   pdf.text('Shift Details', 40, currentY);
   currentY += 20;
   
   if (viewEmployee.shifts && viewEmployee.shifts.length > 0) {
     // Prepare shift table data
     const shiftColumns = ['Date', 'Start Time', 'End Time', 'Duration'];
     const shiftData = viewEmployee.shifts.map(s => {
       const start = new Date(s.startTime);
       const end = new Date(s.endTime);
       return [
         toISO(start),
         start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
         end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
         s.duration,
         {/*s.status,*/},
         {/*s.substore*/}
       ];
     });
     
     // Add shift table to PDF
     const shiftTableResult = autoTable(pdf, {
       head: [shiftColumns],
       body: shiftData,
       startY: currentY,
       styles: {
         fontSize: 9,
         cellPadding: 3,
       },
       headStyles: {
         fillColor: [41, 128, 185],
         textColor: 255,
         fontStyle: 'bold'
       },
       alternateRowStyles: {
         fillColor: [245, 245, 245]
       },
       margin: { top: currentY, right: 40, bottom: 40, left: 40 },
       tableWidth: 'auto'
     });
     
     // Update currentY to position after the shift table with proper spacing
     if (shiftTableResult && shiftTableResult.finalY) {
       currentY = shiftTableResult.finalY + 30;
     } else {
       currentY = currentY + 100;
     }
   } else {
     // No shifts - show message and update currentY
     pdf.setFontSize(10);
     pdf.text('No shifts found in selected range.', 40, currentY);
     currentY += 25; // Add spacing after message
   }
  
   // Add new page for Leaves Section
   pdf.addPage();
   currentY = 40;

   // Add Leaves Section
   pdf.setFontSize(14);
   pdf.text('Leave Records', 40, currentY);
   currentY += 20;
   
   // Filter leaves in date range
   const filteredLeaves = employeeLeaves && employeeLeaves.length > 0 ? employeeLeaves.filter(v => {
     const vacStartStr = v.start_date || v.start || v.from;
     const vacEndStr = v.end_date || v.end || v.to;
     if (!vacStartStr || !vacEndStr) return false;
     const vacStart = new Date(vacStartStr);
     const vacEnd = new Date(vacEndStr);
     const rangeStart = new Date(dateFrom);
     const rangeEnd = new Date(dateTo);
     vacStart.setHours(0,0,0,0);
     vacEnd.setHours(23,59,59,999);
     rangeStart.setHours(0,0,0,0);
     rangeEnd.setHours(23,59,59,999);
     return vacStart <= rangeEnd && vacEnd >= rangeStart;
   }) : [];
   
   if (filteredLeaves.length > 0) {
     // Prepare leaves table data
     const leaveColumns = ['Start Date', 'End Date', 'Type'/*, 'Substore'*/];
     const leaveData = filteredLeaves.map((v, i) => {
       const vacStartStr = v.start_date || v.start || v.from;
       const vacEndStr = v.end_date || v.end || v.to;
       const vacStart = new Date(vacStartStr);
       const vacEnd = new Date(vacEndStr);
       const rangeStart = new Date(dateFrom);
       const rangeEnd = new Date(dateTo);
       vacStart.setHours(0,0,0,0);
       vacEnd.setHours(23,59,59,999);
       rangeStart.setHours(0,0,0,0);
       rangeEnd.setHours(23,59,59,999);
       const overlaps = vacStart <= rangeEnd && vacEnd >= rangeStart;
       const fullyInside = vacStart >= rangeStart && vacEnd <= rangeEnd;
       const leaveType = overlaps ? (fullyInside ? 'Vacation' : 'Leave') : 'Vacation';
       
       return [
         vacStartStr || '',
         vacEndStr || '',
         leaveType,
         {/*v.substore*/}
       ];
     });
     
     // Add leaves table to PDF
     const leaveTableResult = autoTable(pdf, {
       head: [leaveColumns],
       body: leaveData,
       startY: currentY,
       styles: {
         fontSize: 9,
         cellPadding: 3,
       },
       headStyles: {
         fillColor: [128, 185, 41],
         textColor: 255,
         fontStyle: 'bold'
       },
       alternateRowStyles: {
         fillColor: [245, 245, 245]
       },
       margin: { top: currentY, right: 40, bottom: 40, left: 40 },
       tableWidth: 'auto'
     });
     
     // Update currentY after leaves table
     if (leaveTableResult && leaveTableResult.finalY) {
       currentY = leaveTableResult.finalY + 30;
     } else {
       currentY = currentY + 100;
     }
   } else {
     // No leaves - show message and update currentY
     pdf.setFontSize(10);
     pdf.text(employeeLeaves && employeeLeaves.length > 0 
       ? 'No leave records found in selected date range.' 
       : 'No leave records found.', 40, currentY);
     currentY += 25; // Add spacing after message
   }

   // Add new page for Swap Shifts Section
   pdf.addPage();
   currentY = 40;

   // Add Swap Shifts Section
   pdf.setFontSize(14);
   pdf.text('Swap Shift Requests', 40, currentY);
   currentY += 20;
   
   if (viewEmployee.swapRequests && viewEmployee.swapRequests.length > 0) {
     // Prepare swap shift table data
     const swapColumns = ['Date', 'Start Time', 'End Time', 'Duration', 'Swapped With'];
     const swapData = viewEmployee.swapRequests.map(s => {
       return [
         toISO(new Date(s.shift.date)),
         s.shift.startTime,
         s.shift.endTime,
         s.shift.duration,
         s.target_employee?.username || 'Unknown'
       ];
     });
     
     // Add swap shift table to PDF
     const swapTableResult = autoTable(pdf, {
       head: [swapColumns],
       body: swapData,
       startY: currentY,
       styles: {
         fontSize: 9,
         cellPadding: 3,
       },
       headStyles: {
         fillColor: [41, 128, 185],
         textColor: 255,
         fontStyle: 'bold'
       },
       alternateRowStyles: {
         fillColor: [245, 245, 245]
       },
       margin: { top: currentY, right: 40, bottom: 40, left: 40 },
       tableWidth: 'auto'
     });
     
     // Update currentY after swap table
     if (swapTableResult && swapTableResult.finalY) {
       currentY = swapTableResult.finalY + 30;
     } else {
       currentY = currentY + 100;
     }
   } else {
     // No swap shifts - show message and update currentY
     pdf.setFontSize(10);
     pdf.text('No swap shift requests found.', 40, currentY);
     currentY += 25; // Add spacing after message
   }
  
  // Save the PDF
  const fileName = `${viewEmployee.name.replace(/\s+/g, '_')}_${viewEmployee.store.replace(/\s+/g, '_')}_report_${dateFrom}_to_${dateTo}.pdf`;
  pdf.save(fileName);
};
  return (
    <MainCard title="Employee Dashboard">
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <TextField
          type="date"
          label="From"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          type="date"
          label="To"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <FormControl size="small" sx={{ minWidth: 150, height: 40 }}>
          <InputLabel>SelectSubstore</InputLabel>
          <Select value={substoreFilter} label="Substore" onChange={(e) => setSubstoreFilter(e.target.value)} sx={{ height: 35, padding: '0 6px' }}>
            {substoreOptions.map(store => store.id && (
              <MenuItem key={store.id} value={store.id}>
                {store.name || store.storeName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Chip label={`Employees: ${employees.length}`} />
        <Button variant="contained" color="primary" startIcon={<DownloadIcon />} onClick={handleExportPDF} sx={{ mb: 2 }}>
          Export to PDF
        </Button>
      </Box>
      <Paper>
        <TableContainer>

          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                {/*<TableCell>Store</TableCell>*/}
                <TableCell>Total Hours</TableCell>
                <TableCell>Hours Worked</TableCell>
                <TableCell>Extra Hours</TableCell>
                <TableCell>View</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Box display="flex" alignItems="center" justifyContent="center" gap={1} py={3}>
                      <CircularProgress size={24} />
                      <Typography>Loading...</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary">No data</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map(row => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.name}</TableCell>
                    {/*<TableCell>{row.store}</TableCell>*/}
                    <TableCell>{formatHoursToHrMin(row.totalHours)}</TableCell>
                    <TableCell>{formatHoursToHrMin(row.hoursWorked)}</TableCell>
                    <TableCell>
                      <Typography component="span" color={parseFloat(row.extraHours) >= 0 ? 'success.main' : 'error.main'}>
                        {parseFloat(row.extraHours) >= 0 ? '+' : ''}{formatHoursToHrMin(row.extraHours)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" color="primary" onClick={() => openView(row)}>
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={viewOpen} onClose={closeView} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{viewEmployee ? `${viewEmployee.name}` : 'Employee'}</span>
          <Button 
            variant="contained" 
            color="primary" 
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleExportEmployeePDF}
            sx={{ ml: 2 }}
          >
            Export to PDF
          </Button>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ '& .MuiTab-root': { minWidth: 'auto', padding: '12px 16px' } }}>
              <Tab label="Shift Details" />
              <Tab label="Leaves" />
              <Tab label="Swap Shifts" />
            </Tabs>
          </Box>

          {activeTab === 0 && (
            viewEmployee && viewEmployee.shifts && viewEmployee.shifts.length > 0 ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Start</TableCell>
                    <TableCell>End</TableCell>
                    <TableCell>Duration</TableCell>
                    {/*<TableCell>Status</TableCell>*/}
                    {/*<TableCell>Substore</TableCell>*/}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {viewEmployee.shifts.map(s => {
                    const start = new Date(s.startTime);
                    const end = new Date(s.endTime);
                    return (
                      <TableRow key={s.id}>
                        <TableCell>{toISO(start)}</TableCell>
                        <TableCell>{start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</TableCell>
                        <TableCell>{end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</TableCell>
                        <TableCell>{s.duration}</TableCell>
                        {/*<TableCell>{s.status}</TableCell>*/}
                        {/*<TableCell>{s.substore}</TableCell>*/} 
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <Typography color="text.secondary">No shifts in selected range.</Typography>
            )
          )}

          {activeTab === 1 && (() => {
              if (leavesLoading) {
                return (
                  <Box display="flex" alignItems="center" justifyContent="center" py={2}>
                    <CircularProgress size={20} />
                  </Box>
                );
              }
              // Filter leaves in date range
              const filteredLeaves = leaves.filter(v => {
                const vacStartStr = v.start_date || v.start || v.from;
                const vacEndStr = v.end_date || v.end || v.to;
                if (!vacStartStr || !vacEndStr) return false;
                const vacStart = new Date(vacStartStr);
                const vacEnd = new Date(vacEndStr);
                const rangeStart = new Date(dateFrom);
                const rangeEnd = new Date(dateTo);
                vacStart.setHours(0,0,0,0);
                vacEnd.setHours(23,59,59,999);
                rangeStart.setHours(0,0,0,0);
                rangeEnd.setHours(23,59,59,999);
                return vacStart <= rangeEnd && vacEnd >= rangeStart;
              });

              if (filteredLeaves.length > 0) {
                return (
                  <Table size="small">
                    {/* ...table rendering using filteredLeaves... */}
                    <TableBody>
                      {filteredLeaves.map((v, i) => (
                        <TableRow key={v.id || i}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell>{v.start_date || v.start || v.from || ''}</TableCell>
                          <TableCell>{v.end_date || v.end || v.to || ''}</TableCell>
                          <TableCell>
                            {/* ...type logic... */}
                            {(() => {
                              const vacStartStr = v.start_date || v.start || v.from;
                              const vacEndStr = v.end_date || v.end || v.to;
                              if (!vacStartStr || !vacEndStr) return 'Vacation';
                              const vacStart = new Date(vacStartStr);
                              const vacEnd = new Date(vacEndStr);
                              const rangeStart = new Date(dateFrom);
                              const rangeEnd = new Date(dateTo);
                              vacStart.setHours(0,0,0,0);
                              vacEnd.setHours(23,59,59,999);
                              rangeStart.setHours(0,0,0,0);
                              rangeEnd.setHours(23,59,59,999);
                              const overlaps = vacStart <= rangeEnd && vacEnd >= rangeStart;
                              if (!overlaps) return 'Vacation';
                              const fullyInside = vacStart >= rangeStart && vacEnd <= rangeEnd;
                              return fullyInside ? 'Vacation' : 'Leave';
                            })()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                );
              }
              return (
                <Typography color="text.secondary">No leaves found in selected range.</Typography>
              );
            })()}

          {activeTab === 2 && (
            viewEmployee && viewEmployee.swapRequests && viewEmployee.swapRequests.length > 0 ? (
            <Table size="small" sx={{ '& .MuiTableCell-root': { whiteSpace: 'nowrap', padding: '8px 12px' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: '100px' }}>Date</TableCell>
                  <TableCell sx={{ minWidth: '80px' }}>Start</TableCell>
                  <TableCell sx={{ minWidth: '80px' }}>End</TableCell>
                  <TableCell sx={{ minWidth: '90px' }}>Duration</TableCell>
                  <TableCell sx={{ minWidth: '130px' }}>Swapped With</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {viewEmployee.swapRequests.map(s => {
                  // Format date
                  const displayDate = s.shift.date ? toISO(new Date(s.shift.date)) : 'N/A';
                  
                  // Format start time
                  let displayStart = 'N/A';
                  if (s.shift.date && s.shift.startTime) {
                    try {
                      const startDateTime = new Date(`${s.shift.date}T${s.shift.startTime}`);
                      displayStart = startDateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                    } catch (e) {
                      displayStart = s.shift.startTime || 'N/A';
                    }
                  }
                  
                  // Format end time
                  let displayEnd = 'N/A';
                  if (s.shift.date && s.shift.endTime) {
                    try {
                      const endDateTime = new Date(`${s.shift.date}T${s.shift.endTime}`);
                      displayEnd = endDateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                    } catch (e) {
                      displayEnd = s.shift.endTime || 'N/A';
                    }
                  }
                  
                  return (
                    <TableRow key={s.id}>
                      <TableCell>{displayDate}</TableCell>
                      <TableCell>{displayStart}</TableCell>
                      <TableCell>{displayEnd}</TableCell>
                      <TableCell>{s.shift.duration || 'N/A'}</TableCell>
                      <TableCell>{s.target_employee?.username || 'Unknown'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <Typography color="text.secondary">No Swap Shifts found in selected range.</Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeView}>Close</Button>
        </DialogActions>
      </Dialog>
    </MainCard>
  );
}


